import React, { useEffect, useState } from "react";
import profile from "../data/profile";
import projets from "../data/projets";
import { useMemo } from "react";
import ProjectCard from "./ProjectCard";

const TypewriterText = ({ text, delay = 100 }) => {
  const [displayText, setDisplayText] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayText((prev) => prev + text[currentIndex]);
        setCurrentIndex((prev) => prev + 1);
      }, delay);
      return () => clearTimeout(timeout);
    }
  }, [currentIndex, text, delay]);

  return (
    <span
      className="typewriter-container"
      style={{
        minWidth: `${text.length * 0.65}ch`,
        display: "inline-block",
        textAlign: "left",
      }}
    >
      <span className="bg-gradient-to-r from-primary via-blue-600 to-indigo-600 bg-clip-text text-transparent font-bold">
        {displayText}
      </span>
      {currentIndex < text.length && (
        <span className="absolute animate-pulse text-primary ml-1 top-0 font-normal">
          |
        </span>
      )}
    </span>
  );
};

const FloatingCard = ({ children, delay = 0 }) => (
  <div
    className="transform transition-all duration-400 ease-out animate-pop"
    style={{
      animationDelay: `${delay}s`,
    }}
  >
    {children}
  </div>
);

const Presentation = (props) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Section Projets en vedette
  const highlightedProjects = useMemo(
    () => projets.filter((p) => p.highlight),
    [],
  );

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-32 lg:pt-40"
      aria-label="Présentation personnelle"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>
      <div className="absolute top-32 left-10 w-32 h-32 bg-primary/5 rounded-full animate-pulse"></div>
      <div
        className="absolute top-40 right-20 w-24 h-24 bg-blue-500/5 rounded-full animate-bounce"
        style={{ animationDelay: "1s" }}
      ></div>
      <div
        className="absolute bottom-40 left-20 w-40 h-40 bg-indigo-500/5 rounded-full animate-pulse"
        style={{ animationDelay: "2s" }}
      ></div>

      <div className="relative z-10 lg:mx-auto lg:max-w-7xl lg:w-full">
        <div className="lg:px-8 px-4">
          <div
            className={`text-center transition-all duration-400 ${
              isVisible
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-6"
            }`}
          >
            <FloatingCard delay={0.05}>
              <div className="flex justify-center mb-8">
                <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/90 backdrop-blur-sm border border-blue-200 shadow-lg">
                  <div className="w-3 h-3 bg-green-400 rounded-full mr-3 animate-pulse"></div>
                  <span className="text-blue-800 font-semibold text-sm">
                    {profile.availability.status}
                  </span>
                </div>
              </div>
            </FloatingCard>

            <FloatingCard delay={0.1}>
              <div className="relative mb-8">
                <h1 className="lg:tracking-tight font-extrabold text-gray-900 lg:text-7xl text-5xl lg:w-4/5 lg:mx-auto leading-tight">
                  Bonjour, je suis <br />
                  <span className="relative inline-block">
                    <TypewriterText text={profile.name} delay={150} />
                  </span>
                </h1>
              </div>
            </FloatingCard>

            <FloatingCard delay={0.15}>
              <p className="mt-6 mx-auto text-gray-600 lg:text-3xl text-2xl font-medium max-w-4xl leading-relaxed">
                <span className="block lg:inline">{profile.title}</span>
                <span className="hidden lg:inline"> • </span>
                <span className="block lg:inline">{profile.subtitle}</span>
              </p>
            </FloatingCard>

            <FloatingCard delay={0.2}>
              <div className="mt-6 flex justify-center items-center text-gray-500 lg:text-xl">
                <div className="flex items-center bg-white/90 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border border-gray-100">
                  <svg
                    className="w-6 h-6 mr-3 text-primary"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {profile.location}
                </div>
              </div>
            </FloatingCard>
          </div>
        </div>

        {/* Section Projets en vedette */}
        {highlightedProjects.length > 0 && (
          <FloatingCard delay={0.25}>
            {/* full-bleed background that spans the viewport */}
            <div className="mt-16">
              <div className="relative left-1/2 -translate-x-1/2 w-screen">
                <div className="bg-white/95 backdrop-blur-xl rounded-lg shadow-md p-8 lg:p-16 border border-white/20">
                  <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-12">
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Projets en vedette
                      </h2>
                      <div className="w-24 h-1 bg-gradient-to-r from-primary to-blue-600 mx-auto rounded-full"></div>
                    </div>
                    <div className="grid lg:grid-cols-2 gap-8 mb-8">
                      {highlightedProjects.map((projet, index) => (
                        <ProjectCard
                          key={projet.id}
                          projet={projet}
                          index={index}
                        />
                      ))}
                    </div>
                    <div className="flex justify-center mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          if (props.setPage) props.setPage("projects");
                          setTimeout(() => {
                            window.scrollTo({
                              top: 0,
                              left: 0,
                              behavior: "smooth",
                            });
                          }, 50);
                        }}
                        className="inline-block px-6 py-3 rounded-lg bg-gradient-to-r from-primary to-blue-600 text-white font-semibold shadow-sm transition-all duration-200 ui-btn"
                      >
                        Voir tous les projets
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </FloatingCard>
        )}

        {/* Centres d'intérêt redesignés */}
        <FloatingCard delay={0.35}>
          <div className="mt-16 text-center">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">
              Mes Passions
            </h3>
            <div className="flex flex-wrap justify-center gap-4 max-w-4xl mx-auto">
              {profile.interests.map((interest, index) => (
                <span
                  key={index}
                  className="group inline-flex items-center px-6 py-3 rounded-full bg-white/90 backdrop-blur-sm text-gray-700 font-medium transition-all duration-300 border border-gray-200/50"
                  style={{ animationDelay: `${1.4 + index * 0.1}s` }}
                >
                  <span className="transition-transform duration-300">
                    {interest}
                  </span>
                </span>
              ))}
            </div>
          </div>
        </FloatingCard>

        {/* CTA Button redesigné */}
        <FloatingCard delay={0.4}>
          <div className="mt-16 mb-20 flex justify-center">
            <a
              href="/CV_Fabien_Lapert.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center px-8 py-4 text-lg font-semibold rounded-lg text-white bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-indigo-600 transition-colors duration-200 shadow-sm overflow-hidden ui-btn"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <span className="relative flex items-center">
                <svg
                  className="mr-3 w-5 h-5 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Télécharger mon CV
              </span>
            </a>
          </div>
        </FloatingCard>
      </div>
    </section>
  );
};

export default Presentation;
